import * as React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormikContext } from 'formik'
import { Autocomplete } from '@mui/material'
import Textfield from '../FormsUI/Textfield'
import { getPseudonymsBySpecieId } from '../../services'

import { Chip } from '@mui/material'
import { setEnablePseudonymField } from '../../redux/slices/Tree/treeFeatures'
import _ from 'lodash'

const InputBasic = ({ data, options, variant, label, editable, ...rest }) => {
  const dispatch = useDispatch()
  const { values, setFieldValue } = useFormikContext()
  const [filterList1, setSelectedList] = React.useState(data)
  const [optionsList, setOptionsList] = React.useState(options)
  const [fixedItems, setFixedItems] = React.useState(() => data.filter(d => d.id))
  const prevSpecieIdRef = React.useRef(undefined)
  const latestValueRef = React.useRef(values?.[rest.name])

  const inputError = useSelector(
    state => state.treeFeatures.enableSaveButton.pseudonymField,
  )
  const specieSelected = useSelector(
    state => state.treeFeatures.enableSaveButton.specieField?.selected,
  )

  const isFixed = item => fixedItems.some(f => f.name === item.name)

  const normalizeList = React.useCallback(list => {
    if (!Array.isArray(list)) return []
    return list
      .map(item => {
        if (!item) return null
        if (typeof item === 'string') return { name: item }
        if (item.name) return item
        if (item.label) return { name: item.label }
        return null
      })
      .filter(Boolean)
  }, [])

  const ensureFixedItems = React.useCallback(
    list => {
      const next = [...list]
      fixedItems.forEach(fixed => {
        if (!next.some(item => item.name === fixed.name)) {
          next.unshift(fixed)
        }
      })
      return next
    },
    [fixedItems],
  )

  const handleOnBlur = () => {
    if (!inputError.error && !Boolean(filterList1.length))
      dispatch(setEnablePseudonymField({ error: true, helperText: 'Obligatorio' }))
  }

  const handleSelection = (_event, newList) => {
    // Keep backend chips always (they can't be removed)
    const backendChips = filterList1.filter(o => o.fromBackend)
    let newListFormat = [...backendChips]
    _.forEach(newList, o => {
      if (o.fromBackend) return // already included
      if (!o.name) {
        const existsPreviously = _.some(newListFormat, ['name', o])
        if (!existsPreviously) newListFormat.push({ name: o })
      } else newListFormat.push(o)
    })
    // Ensure fixed items are always present
    newListFormat = ensureFixedItems(newListFormat)
    setSelectedList(newListFormat)
    setFieldValue(rest.name, newListFormat)

    !newListFormat.length
      ? dispatch(setEnablePseudonymField({ error: true, helperText: 'Obligatorio' }))
      : dispatch(setEnablePseudonymField({ error: false, selected: newListFormat }))
  }

  const handelEqualValue = (option, value) => option.name === value.name

  React.useEffect(() => {
    dispatch(setEnablePseudonymField({ error: false, selected: filterList1 }))
    return () => {
      if (!latestValueRef.current?.length)
        dispatch(setEnablePseudonymField({ error: false, selected: undefined }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    latestValueRef.current = values?.[rest.name] || []
  }, [values?.[rest.name], rest.name])

  React.useEffect(() => {
    if (!values?.[rest.name]?.length && data?.length) {
      setFieldValue(rest.name, data)
    }
  }, [values?.[rest.name], data, rest.name, setFieldValue])

  React.useEffect(() => {
    if (!values?.[rest.name]) return
    const normalized = ensureFixedItems(normalizeList(values[rest.name]))
    setSelectedList(normalized)
  }, [values?.[rest.name], normalizeList, ensureFixedItems])

  React.useEffect(() => {
    if (!specieSelected?.id) return
    const specieId = parseInt(specieSelected.id, 10)
    if (prevSpecieIdRef.current === specieId) return

    const isFirstLoad = prevSpecieIdRef.current === undefined
    prevSpecieIdRef.current = specieId

    getPseudonymsBySpecieId(specieId)
      .then(pseudonymsOfSpecie => {
        setOptionsList(pseudonymsOfSpecie)
        // Edit form initial load: keep original data, only update options
        if (isFirstLoad && data.length > 0) return
        // Species changed or registration first selection: replace fixed + selected
        setFixedItems(pseudonymsOfSpecie)
        setSelectedList(pseudonymsOfSpecie)
        setFieldValue(rest.name, pseudonymsOfSpecie)
        dispatch(
          setEnablePseudonymField(
            pseudonymsOfSpecie.length
              ? { error: false, selected: pseudonymsOfSpecie }
              : { error: true, helperText: 'Obligatorio' },
          ),
        )
      })
      .catch(err => console.log('Error :>> ', err))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specieSelected])

  return (
    <Autocomplete
      multiple
      id={`pseudonym-inputs`}
      size="small"
      name={rest.name}
      value={filterList1}
      onChange={handleSelection}
      options={optionsList}
      readOnly={!editable}
      getOptionLabel={option => option.name}
      isOptionEqualToValue={handelEqualValue}
      onBlur={handleOnBlur}
      freeSolo
      disableClearable
      renderTags={(value, getTagProps) => {
        return value.map((option, index) => {
          if (!option.name) option = { name: option }
          const { key, ...chipProps } = getTagProps({ index })
          const fixed = isFixed(option)
          if (fixed) delete chipProps.onDelete
          return (
            <Chip
              key={key}
              variant="outlined"
              label={option.name}
              {...chipProps}
              disabled={fixed}
            />
          )
        })
      }}
      renderInput={params => {
        return (
          <Textfield
            {...params}
            name={rest.name}
            value={filterList1}
            variant={variant}
            label={label}
            placeholder={editable ? 'Sugerir nuevos' : undefined}
            error={inputError.error}
            helperText={inputError.helperText}
          />
        )
      }}
      style={{ width: '100%' }}
    />
  )
}
export default InputBasic
